import { useState, type FormEvent } from "react";
import { Badge, Button, Card, Checkbox, FormRow, Modal, ModalFooter, Row, SelectField, TextField, useToast, type BadgeTone } from "@plataforma/design-system";
import { fiscalDefaultEndpoints, type CertificateStatus, type FiscalEnvironment, type FiscalSettings, type FiscalSettingsInput } from "@plataforma/shared";
import { useAppConfig } from "../../app/config";
import { errorMessage } from "../../lib/api";
import { useFiscalSettings, useInstallCertificate, useRemoveCertificate, useSaveFiscalSettings } from "../invoices/api";

const toInput = (settings: FiscalSettings): FiscalSettingsInput => ({ providerName: settings.providerName, cnpj: settings.cnpj, city: settings.city, serviceCode: settings.serviceCode, descriptionTemplate: settings.descriptionTemplate, environment: settings.environment, endpoint: settings.endpoint, simulateFailure: settings.simulateFailure });
const certificateTone: Record<CertificateStatus, BadgeTone> = { missing: "danger", expired: "danger", expiring: "warning", valid: "success" };

export function FiscalSection() {
  const fiscal = useFiscalSettings();
  if (!fiscal.data) return <Card padded title="Emissão de notas fiscais"><p className="ds-muted ds-small" style={{ marginTop: 12 }}>Carregando…</p></Card>;
  // A chave recria o formulário quando a configuração salva muda, em vez de sincronizar estado por efeito.
  return <FiscalEditor key={JSON.stringify(toInput(fiscal.data))} settings={fiscal.data} />;
}

function FiscalEditor({ settings }: { settings: FiscalSettings }) {
  const { format } = useAppConfig();
  const toast = useToast();
  const save = useSaveFiscalSettings();
  const removeCertificate = useRemoveCertificate();
  const [values, setValues] = useState<FiscalSettingsInput>(toInput(settings));
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const set = (field: keyof FiscalSettingsInput) => (event: { target: { value: string } }) => setValues({ ...values, [field]: event.target.value });
  const certificateLabel = settings.certificateStatus === "missing" ? "Não instalado" : settings.certificateStatus === "expired" ? `Vencido em ${format.date(settings.certificate?.validUntil ?? "")}` : settings.certificateStatus === "expiring" ? `Vence em ${settings.certificateDaysLeft} dias` : `Válido até ${format.date(settings.certificate?.validUntil ?? "")}`;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await save.mutateAsync(values);
      toast("Configuração fiscal salva.");
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  return (
    <Card padded title="Emissão de notas fiscais">
      <div className="settings-line" style={{ borderTop: 0, marginTop: 8 }}><span>Emissor</span><span className="ds-muted">{settings.issuer}</span></div>
      <div className="settings-line">
        <span>Certificado digital A1{settings.certificate ? <span className="ds-muted ds-small" style={{ display: "block" }}>{settings.certificate.fileName} · {settings.certificate.subject}<br />enviado por {settings.certificate.installedByName || "equipe"}</span> : null}</span>
        <span style={{ display: "grid", justifyItems: "end", gap: 8 }}>
          <Badge tone={certificateTone[settings.certificateStatus]}>{certificateLabel}</Badge>
          <Row align="end">
            <Button size="small" variant={settings.certificate ? "default" : "primary"} onClick={() => setUploading(true)}>{settings.certificate ? "Trocar certificado" : "Enviar certificado"}</Button>
            {settings.certificate ? <Button variant="quiet" tone="danger" onClick={() => { if (window.confirm("Remover o certificado? A emissão fica bloqueada até enviar outro.")) removeCertificate.mutate(undefined, { onSuccess: () => toast("Certificado removido."), onError: (failure) => toast(errorMessage(failure)) }); }}>Remover</Button> : null}
          </Row>
        </span>
      </div>
      <form onSubmit={submit} style={{ marginTop: 14 }}>
        <FormRow>
          <TextField label="Prestador (razão social)" maxLength={150} value={values.providerName} onChange={set("providerName")} />
          <TextField label="CNPJ" maxLength={20} value={values.cnpj} onChange={set("cnpj")} />
        </FormRow>
        <FormRow>
          <TextField label="Município" maxLength={80} value={values.city} onChange={set("city")} />
          <TextField label="Código de serviço" maxLength={40} value={values.serviceCode} onChange={set("serviceCode")} hint="Definido pela contabilidade." />
        </FormRow>
        <FormRow>
          <SelectField label="Ambiente do emissor" value={values.environment} hint={values.environment === "production" ? "Notas com validade fiscal." : "Notas de teste, sem validade fiscal."}
            onChange={(event) => { const environment = event.target.value as FiscalEnvironment; setValues({ ...values, environment, endpoint: fiscalDefaultEndpoints[environment] }); }}>
            <option value="restricted">Produção restrita (testes do governo)</option>
            <option value="production">Produção</option>
          </SelectField>
          <TextField label="Endereço da API do emissor" type="url" maxLength={200} spellCheck={false} autoComplete="off" value={values.endpoint} onChange={set("endpoint")}
            hint={values.endpoint === fiscalDefaultEndpoints[values.environment] ? "Endereço padrão deste ambiente." : <Button variant="quiet" onClick={() => setValues({ ...values, endpoint: fiscalDefaultEndpoints[values.environment] })}>Restaurar o padrão</Button>} />
        </FormRow>
        <TextField label="Descrição padrão do serviço" maxLength={200} required value={values.descriptionTemplate} onChange={set("descriptionTemplate")} hint="{tipo} e {data} são preenchidos com a consulta. Pode ser ajustada em cada nota." />
        {settings.issuer === "Simulado" ? <Checkbox label="Simular falha do emissor" hint="Só com o emissor simulado, para ver o que acontece quando a emissão não passa." checked={values.simulateFailure} onChange={(event) => setValues({ ...values, simulateFailure: event.target.checked })} /> : null}
        {error ? <p className="ds-field__error" role="alert">{error}</p> : null}
        <Row align="end"><Button type="submit" variant="primary" disabled={save.isPending}>Salvar configuração</Button></Row>
      </form>
      {uploading ? <CertificateForm replacing={Boolean(settings.certificate)} onClose={() => setUploading(false)} /> : null}
    </Card>
  );
}

/** Lê o arquivo escolhido como base64, sem expor caminho nem conteúdo fora do envio. */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function CertificateForm({ replacing, onClose }: { replacing: boolean; onClose: () => void }) {
  const { format } = useAppConfig();
  const toast = useToast();
  const install = useInstallCertificate();
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!file) return setError("Escolha o arquivo do certificado (.pfx ou .p12).");
    if (!/\.(pfx|p12)$/i.test(file.name)) return setError("O arquivo precisa terminar em .pfx ou .p12.");
    if (!password) return setError("Informe a senha do certificado.");
    try {
      const settings = await install.mutateAsync({ fileName: file.name, contentBase64: await fileToBase64(file), password });
      toast(`Certificado instalado. Válido até ${format.date(settings.certificate?.validUntil ?? "")}.`);
      onClose();
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }
  return (
    <Modal title={replacing ? "Trocar certificado digital" : "Enviar certificado digital"} onClose={onClose} dirty={Boolean(file) || password.length > 0}>
      <form onSubmit={submit} noValidate>
        <p className="ds-muted ds-small" style={{ marginBottom: 14 }}>O certificado A1 é o arquivo que a certificadora entregou na compra (termina em .pfx ou .p12), junto com a senha criada naquele momento. Ele permite ao sistema assinar as notas em nome do consultório.</p>
        <TextField label="Arquivo do certificado" type="file" accept=".pfx,.p12" onChange={(event) => setFile(event.target.files?.[0] ?? null)} hint="Escolha o arquivo no seu computador. Não é preciso informar pasta nem caminho." />
        <TextField label="Senha do certificado" type="password" autoComplete="off" value={password} onChange={(event) => setPassword(event.target.value)} hint="O certificado e a senha ficam guardados cifrados no servidor e não voltam a ser exibidos." />
        {error ? <p className="ds-field__error" role="alert">{error}</p> : null}
        <ModalFooter>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={install.isPending}>Enviar certificado</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
